import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const LLP = () => {
  const data = {
    title: 'LLP Registration',
    description: 'Register your Limited Liability Partnership with minimal compliance and maximum flexibility. Get complete legal protection with the benefits of partnership structure.',
    keywords: 'LLP registration, Limited Liability Partnership, LLP formation, business registration India, partnership firm, LLP vs Private Limited',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          A Limited Liability Partnership (LLP) is a corporate business entity that combines the flexibility of a traditional 
          partnership with the benefits of limited liability. Introduced in India through the LLP Act 2008, it has become one 
          of the most preferred business structures for professionals, startups, and small to medium enterprises. LLP offers 
          the best of both worlds – operational flexibility with legal protection.
        </p>
        <p>
          <strong>Why Choose LLP?</strong> LLP is ideal for professional services firms (CAs, lawyers, consultants), 
          family businesses, and ventures where partners want flexibility without the heavy compliance burden of a 
          Private Limited Company. Partners in an LLP have limited liability, meaning personal assets are protected 
          from business debts and obligations.
        </p>
        <p>
          <strong>Legal Framework:</strong> LLPs in India are governed by the Limited Liability Partnership Act, 2008 
          and regulated by the Ministry of Corporate Affairs (MCA). Unlike traditional partnerships, an LLP is a separate 
          legal entity, can own property, sue or be sued, and has perpetual succession. The LLP Agreement governs the 
          mutual rights and duties of partners.
        </p>
        <p>
          <strong>Tax Benefits:</strong> LLPs enjoy favorable tax treatment. Profits are taxed at a flat rate of 30% 
          (plus surcharge and cess), and there's no Dividend Distribution Tax (DDT). Partners can receive remuneration 
          and interest on capital as deductible expenses, reducing overall tax liability. No Minimum Alternate Tax (MAT) 
          applies if LLP books are not required to be audited.
        </p>
      </div>
    ),
    
    benefits: [
      'Limited liability for all partners – personal assets protected from business liabilities',
      'No minimum capital requirement – start with any amount of capital contribution',
      'Lower compliance requirements compared to Private Limited Company',
      'Flexibility in internal management governed by LLP Agreement',
      'Separate legal entity with perpetual succession – continues regardless of partner changes',
      'Tax benefits – no DDT, partners can receive tax-deductible remuneration',
      'Easy to form and manage with minimal regulatory intervention',
      'Partners can participate in management without losing limited liability',
      'Can have unlimited number of partners (minimum 2 required)',
      'Easy conversion to Private Limited Company if needed for fundraising',
      'No requirement for statutory audit if turnover < ₹40 lakhs and capital < ₹25 lakhs',
      'Profits can be distributed as per LLP Agreement without additional tax',
    ],
    
    documents: [
      'PAN Card of all Designated Partners (mandatory)',
      'Aadhaar Card of all Partners (for identity verification)',
      'Passport (for foreign nationals/NRIs) with valid Visa',
      'Passport size photographs (recent, colored - 2 copies per partner)',
      'Address proof of registered office: Rent agreement (notarized) or Sale deed',
      'Utility bill of registered office (electricity/water bill - not older than 2 months)',
      'NOC (No Objection Certificate) from property owner',
      'Digital Signature Certificate (DSC) of Designated Partners',
      'Proof of registered office address (bank statement/passbook)',
      'Consent to act as Designated Partner (Form-9)',
      'Subscriber sheet signed by all partners',
    ],
    
    process: [
      'Apply for Digital Signature Certificate (DSC) for all designated partners – 1-2 days',
      'Obtain Designated Partner Identification Number (DPIN) from MCA – 1 day',
      'Name reservation through RUN-LLP service – submit 2 name options, approved in 1-2 days',
      'Drafting of LLP Agreement covering profit sharing, rights, duties, and management',
      'Filing of FiLLiP form (Form for incorporation of LLP) with MCA',
      'Upload incorporation documents including subscriber sheet and consent forms',
      'Certificate of Incorporation issued by ROC with LLPIN',
      'File LLP Agreement within 30 days of incorporation (Form 3)',
      'Apply for PAN and TAN for the LLP',
      'Open bank account with Certificate of Incorporation',
    ],
    
    timeline: '10-12 Working Days',
    pricing: '₹5,999 + Govt Fees',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Government Fees (Approximate):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Name Reservation Fee: ₹200</li>
          <li>Incorporation Fee (capital up to ₹1 lakh): ₹500</li>
          <li>LLP Agreement Filing (Form 3): ₹100</li>
          <li>DSC (per designated partner): ₹1,500 - ₹2,000</li>
          <li>Stamp Duty on LLP Agreement: Varies by state (₹500 - ₹2,000)</li>
        </ul>
        <p className="mt-3"><strong>Annual Compliance Requirements:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Form 11 (Annual Return) – Due by 30th May every year</li>
          <li>Form 8 (Statement of Account & Solvency) – Due by 30th October</li>
          <li>Income Tax Return filing – Due by 31st July (or 30th September if audit required)</li>
          <li>Tax Audit if turnover exceeds ₹1 crore (or ₹10 crore with digital transactions)</li>
        </ul>
        <p className="mt-3"><strong>Our Package Includes:</strong> DSC application, DPIN application, name search and reservation, 
        LLP Agreement drafting, incorporation filing, PAN/TAN application, and compliance guidance.</p>
      </div>
    ),
    
    faqs: [
      {
        question: 'What is the minimum number of partners required for LLP?',
        answer: 'An LLP requires a minimum of 2 designated partners (at least one must be Indian resident). There is no maximum limit on the number of partners. Every LLP must have at least 2 designated partners who are responsible for compliance.',
      },
      {
        question: 'What is the difference between LLP and Partnership Firm?',
        answer: 'LLP provides limited liability to all partners and is a separate legal entity registered under LLP Act 2008. Traditional Partnership has unlimited liability for partners and is governed by Partnership Act 1932. LLP has perpetual succession while Partnership dissolves on partner exit.',
      },
      {
        question: 'Is LLP Agreement mandatory?',
        answer: 'Yes, every LLP must file its LLP Agreement with the ROC within 30 days of incorporation. The LLP Agreement governs the rights, duties, profit sharing, and management of the LLP. If not filed in time, default provisions of LLP Act apply.',
      },
      {
        question: 'Can a company or another LLP be a partner in LLP?',
        answer: 'Yes, a body corporate (including companies and other LLPs) can become a partner in an LLP. However, designated partners must be individuals (natural persons) with at least one being an Indian resident.',
      },
      {
        question: 'What are the tax implications for LLP?',
        answer: 'LLP is taxed at a flat rate of 30% (plus surcharge if income > ₹1 crore and 4% cess). Partners can receive remuneration (deductible expense) and interest on capital (up to 12%). No Dividend Distribution Tax. Profits distributed to partners are not taxed again.',
      },
      {
        question: 'Can LLP raise funds from investors?',
        answer: 'LLPs cannot issue shares like companies, so raising equity funding from VCs/investors is limited. LLPs typically raise funds through partner contributions or loans. For significant funding needs, conversion to Private Limited Company is recommended.',
      },
      {
        question: 'When is audit mandatory for LLP?',
        answer: 'Statutory audit is mandatory if LLP turnover exceeds ₹40 lakhs OR capital contribution exceeds ₹25 lakhs in any financial year. Tax audit is required if turnover exceeds ₹1 crore (or ₹10 crore if 95% transactions are digital).',
      },
      {
        question: 'Can LLP be converted to Private Limited Company?',
        answer: 'Yes, LLP can be converted to Private Limited Company under Section 366 of Companies Act 2013. This is often done when the business needs to raise venture capital or private equity. All partners become shareholders and the LLP ceases to exist.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default LLP;
